import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  CircularProgress,
  Chip,
  Alert,
  IconButton,
  Card,
  CardContent,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
} from '@mui/material';
import {
  SupportAgent,
  HelpOutline,
  CheckCircleOutline,
  ArrowBack,
  History,
  Close,
  FilterList,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import AdminApi from '../../Service/Admin.api';
import CaseApi from 'Service/Case.api';
import ColorData from 'Components/CstmTheme/ColorData';
import { useSelector } from 'react-redux';

const RaiseComplaint = () => {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);

  const { myInfo } = useSelector((state: any) => state?.user || {});
  const isAdmin = ["ADMIN", "SUPERADMIN"].includes(myInfo?.userType);

  // Form State
  const [category, setCategory] = useState('Service Delay');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [caseNumber, setCaseNumber] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [successTicketId, setSuccessTicketId] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // User active cases state
  const [userCases, setUserCases] = useState<any[]>([]);
  const [loadingCases, setLoadingCases] = useState(false);

  // Tickets List State
  const [tickets, setTickets] = useState<any[]>([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedTicket, setSelectedTicket] = useState<any | null>(null);

  // Admin Target User State
  const [targetUserId, setTargetUserId] = useState<number | null>(null);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [userOptions, setUserOptions] = useState<any[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const categories = ['Billing', 'Service Delay', 'Tech Issue', 'Other'];

  const fetchCasesForUser = async (userId: number) => {
    if (!userId) return;
    setLoadingCases(true);
    try {
      const res = await CaseApi.getCaseListByUserId({ clientId: userId, pageNumber: 1, pageSize: 100 });
      if (res?.success && Array.isArray(res.data?.data)) {
        setUserCases(res.data.data);
      } else {
        setUserCases([]);
      }
    } catch (err) {
      console.error("Error fetching cases for user:", err);
      setUserCases([]);
    } finally {
      setLoadingCases(false);
    }
  };

  useEffect(() => {
    if (!isAdmin && myInfo?.id) {
      fetchCasesForUser(myInfo.id);
    }
  }, [myInfo, isAdmin]);

  useEffect(() => {
    if (isAdmin && targetUserId) {
      fetchCasesForUser(targetUserId);
    } else if (isAdmin && !targetUserId) {
      setUserCases([]);
    }
  }, [targetUserId, isAdmin]);

  useEffect(() => {
    if (isAdmin && userSearchTerm.length > 1) {
      const delayDebounceFn = setTimeout(() => {
        setLoadingUsers(true);
        // Searching across all user types to be flexible
        AdminApi.getAllUsers("", 1, 20, userSearchTerm).then((res: any) => {
          if (res.success && res.data) {
            if (res.data.data && Array.isArray(res.data.data)) {
              setUserOptions(res.data.data);
            } else if (Array.isArray(res.data)) {
              setUserOptions(res.data);
            } else {
              setUserOptions([]);
            }
          } else {
            setUserOptions([]);
          }
          setLoadingUsers(false);
        }).catch(() => setLoadingUsers(false));
      }, 500);
      return () => clearTimeout(delayDebounceFn);
    }
  }, [isAdmin, userSearchTerm]);

  useEffect(() => {
    if (tabValue === 1) {
      fetchMyTickets();
    }
  }, [tabValue, page, pageSize, statusFilter]);

  const fetchMyTickets = async () => {
    setTicketsLoading(true);
    try {
      const res = await AdminApi.getMyTickets(page + 1, pageSize, statusFilter);
      if (res.success && res.data) {
        setTickets(res.data.data || []);
        setTotalCount(res.data.totalCount || 0);
      }
    } catch (error) {
      console.error('Error fetching my tickets:', error);
    } finally {
      setTicketsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!subject.trim() || !description.trim()) {
      setErrorMsg('Please fill in the subject and description of your complaint.');
      return;
    }

    setSubmitting(true);
    setErrorMsg(null);

    try {
      const payload: any = {
        category,
        subject: subject.trim(),
        description: description.trim(),
        caseNumber: caseNumber.trim() || null,
      };

      if (isAdmin && targetUserId) {
        payload.userId = targetUserId;
      }

      const res = await AdminApi.createTicket(payload);
      if (res.success && res.data) {
        setSuccessTicketId(res.data.ticketId);
        // Reset form
        setSubject('');
        setDescription('');
        setCaseNumber('');
      } else {
        setErrorMsg(res.alert?.alertMessage || 'Failed to submit complaint. Please try again.');
      }
    } catch (error: any) {
      console.error('Error submitting ticket:', error);
      setErrorMsg('An unexpected error occurred. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'open':
        return 'error';
      case 'in progress':
      case 'inprogress':
        return 'warning';
      case 'resolved':
        return 'success';
      case 'closed':
      default:
        return 'default';
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ p: 4, maxWidth: tabValue === 0 ? 800 : 1200, mx: 'auto', minHeight: '80vh' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate(-1)} sx={{ mr: 2, bgcolor: 'rgba(40, 49, 101, 0.08)', '&:hover': { bgcolor: 'rgba(40, 49, 101, 0.15)' } }}>
          <ArrowBack sx={{ color: '#283165' }} />
        </IconButton>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, fontFamily: '"Outfit", sans-serif', color: '#1e293b', display: 'flex', alignItems: 'center', gap: 1 }}>
            <SupportAgent sx={{ fontSize: 30, color: '#EF4444' }} /> Raise a Complaint
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b' }}>
            Submit a support ticket and our team will get back to you shortly.
          </Typography>
        </Box>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 4, borderRadius: '16px', overflow: 'hidden', border: '1px solid rgba(226, 232, 240, 0.8)' }}>
        <Tabs
          value={tabValue}
          onChange={(e, val) => {
            setTabValue(val);
            setPage(0);
            setSuccessTicketId(null);
          }}
          variant="fullWidth"
          sx={{
            '& .MuiTabs-indicator': { bgcolor: '#283165', height: 3 },
            '& .MuiTab-root': {
              fontFamily: '"Outfit", sans-serif',
              fontWeight: 600,
              fontSize: '1rem',
              textTransform: 'none',
              py: 2,
              color: '#64748b',
              gap: 1,
              '&.Mui-selected': { color: '#283165' },
            }
          }}
        >
          <Tab label="Raise a New Complaint" icon={<SupportAgent sx={{ fontSize: 20 }} />} iconPosition="start" />
          <Tab label="My Support Tickets" icon={<History sx={{ fontSize: 20 }} />} iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Tab 0: Raise Complaint Form */}
      {tabValue === 0 && (
        successTicketId ? (
          <Card
            elevation={0}
            sx={{
              p: 4,
              borderRadius: '24px',
              border: '1px solid #2e7d3220',
              background: 'linear-gradient(135deg, #f1f8e9 0%, #ffffff 100%)',
              textAlign: 'center',
              boxShadow: '0 10px 30px rgba(46, 125, 50, 0.05)',
            }}
          >
            <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
              <CheckCircleOutline sx={{ fontSize: 70, color: '#2e7d32', mb: 2 }} />
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, color: '#1b5e20', fontFamily: '"Outfit", sans-serif' }}>
                Complaint Registered Successfully!
              </Typography>
              <Typography variant="body1" sx={{ color: '#455a64', mb: 3 }}>
                Your ticket has been logged and queued. Our support team is reviewing your issue.
              </Typography>

              <Box
                sx={{
                  bgcolor: 'white',
                  border: '1.5px dashed #2e7d32',
                  borderRadius: '16px',
                  px: 4,
                  py: 2,
                  mb: 4,
                  display: 'inline-block',
                }}
              >
                <Typography variant="caption" sx={{ color: '#64748b', textTransform: 'uppercase', fontWeight: 600, letterSpacing: 1 }}>
                  Ticket Reference
                </Typography>
                <Typography variant="h6" sx={{ color: '#1b5e20', fontWeight: 800, letterSpacing: 0.5, mt: 0.5 }}>
                  {successTicketId}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
                <Button
                  variant="contained"
                  onClick={() => setSuccessTicketId(null)}
                  sx={{
                    bgcolor: '#283165',
                    color: 'white',
                    px: 3,
                    py: 1.5,
                    borderRadius: '12px',
                    fontWeight: 600,
                    textTransform: 'none',
                    '&:hover': { bgcolor: '#1c234b' },
                  }}
                >
                  Raise Another Complaint
                </Button>
                <Button
                  variant="contained"
                  onClick={() => {
                    setSuccessTicketId(null);
                    setTabValue(1);
                  }}
                  sx={{
                    bgcolor: '#EF4444',
                    color: 'white',
                    px: 3,
                    py: 1.5,
                    borderRadius: '12px',
                    fontWeight: 600,
                    textTransform: 'none',
                    boxShadow: '0 4px 14px rgba(239, 68, 68, 0.2)',
                    '&:hover': { bgcolor: '#dc2626' },
                  }}
                >
                  View My Tickets
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/dashboard')}
                  sx={{
                    borderColor: '#cbd5e1',
                    color: '#475569',
                    px: 3,
                    py: 1.5,
                    borderRadius: '12px',
                    fontWeight: 600,
                    textTransform: 'none',
                    '&:hover': { borderColor: '#94a3b8', bgcolor: 'rgba(0, 0, 0, 0.02)' },
                  }}
                >
                  Go to Dashboard
                </Button>
              </Box>
            </CardContent>
          </Card>
        ) : (
          <Paper
            elevation={0}
            sx={{
              p: 4,
              borderRadius: '24px',
              border: '1px solid rgba(226, 232, 240, 0.8)',
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.03)',
              bgcolor: 'white',
            }}
          >
            {errorMsg && (
              <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }}>
                {errorMsg}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                {/* Target User (Admin Only) */}
                {isAdmin && (
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#475569', mb: 1 }}>
                      Raise On Behalf Of (Select User)
                    </Typography>
                    <Autocomplete
                      options={userOptions}
                      getOptionLabel={(option: any) => `${option.firstName} ${option.lastName} (${option.email})`}
                      loading={loadingUsers}
                      onInputChange={(event, newInputValue) => {
                        setUserSearchTerm(newInputValue);
                      }}
                      onChange={(event, newValue) => {
                        setTargetUserId(newValue ? newValue.id : null);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Search user by name or email..."
                          variant="outlined"
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <React.Fragment>
                                {loadingUsers ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                              </React.Fragment>
                            ),
                            sx: {
                              borderRadius: '12px',
                              bgcolor: '#f8fafc',
                              '& fieldset': { borderColor: '#e2e8f0' },
                              '&:hover fieldset': { borderColor: '#cbd5e1' },
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>
                )}

                {/* Category Selector */}
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#475569', mb: 1.5 }}>
                    Select Category
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                    {categories.map((cat) => {
                      const isActive = category === cat;
                      return (
                        <Chip
                          key={cat}
                          label={cat}
                          onClick={() => setCategory(cat)}
                          sx={{
                            fontSize: '0.9rem',
                            fontWeight: 600,
                            px: 2,
                            py: 2.2,
                            borderRadius: '12px',
                            border: `1.5px solid ${isActive ? '#EF4444' : '#e2e8f0'}`,
                            bgcolor: isActive ? '#FEF2F2' : '#f8fafc',
                            color: isActive ? '#EF4444' : '#64748b',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              bgcolor: isActive ? '#FEF2F2' : '#f1f5f9',
                              borderColor: isActive ? '#EF4444' : '#cbd5e1',
                            },
                          }}
                        />
                      );
                    })}
                  </Box>
                </Grid>

                {/* Subject Input */}
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#475569', mb: 1 }}>
                    Subject
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="Brief summary of the issue"
                    variant="outlined"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    InputProps={{
                      sx: {
                        borderRadius: '12px',
                        bgcolor: '#f8fafc',
                        '& fieldset': { borderColor: '#e2e8f0' },
                        '&:hover fieldset': { borderColor: '#cbd5e1' },
                      },
                    }}
                  />
                </Grid>

                {/* Case Ref (Optional Dropdown) */}
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#475569', mb: 1 }}>
                    Case Reference # (Optional)
                  </Typography>
                  <FormControl fullWidth variant="outlined">
                    <Select
                      value={caseNumber}
                      onChange={(e) => setCaseNumber(e.target.value)}
                      displayEmpty
                      disabled={loadingCases}
                      sx={{
                        borderRadius: '12px',
                        bgcolor: '#f8fafc',
                        '& fieldset': { borderColor: '#e2e8f0' },
                        '&:hover fieldset': { borderColor: '#cbd5e1' },
                      }}
                    >
                      <MenuItem value="">
                        <em>None / General Complaint</em>
                      </MenuItem>
                      {userCases.map((c: any) => {
                        const refVal = c.caseNumber || String(c.pkCase);
                        const label = c.caseNumber
                          ? `${c.caseNumber} (${c.orderTypeName || c.caseTypeName || 'Case'})`
                          : `Case #${c.pkCase} (${c.orderTypeName || c.caseTypeName || 'Case'})`;
                        return (
                          <MenuItem key={c.pkCase} value={refVal}>
                            {label}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Description Input */}
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#475569', mb: 1 }}>
                    Description
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={5}
                    placeholder="Describe your issue in detail so our support team can help you..."
                    variant="outlined"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    InputProps={{
                      sx: {
                        borderRadius: '12px',
                        bgcolor: '#f8fafc',
                        '& fieldset': { borderColor: '#e2e8f0' },
                        '&:hover fieldset': { borderColor: '#cbd5e1' },
                      },
                    }}
                  />
                </Grid>

                {/* Submit Buttons */}
                <Grid size={{ xs: 12 }} sx={{ display: 'flex', gap: 2, mt: 1 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={submitting}
                    sx={{
                      bgcolor: '#EF4444',
                      color: 'white',
                      px: 5,
                      py: 1.5,
                      borderRadius: '12px',
                      fontWeight: 700,
                      textTransform: 'none',
                      fontSize: '1rem',
                      flex: 2,
                      boxShadow: '0 4px 14px rgba(239, 68, 68, 0.3)',
                      '&:hover': {
                        bgcolor: '#dc2626',
                        boxShadow: '0 6px 20px rgba(239, 68, 68, 0.4)',
                      },
                      '&:disabled': {
                        bgcolor: 'rgba(239, 68, 68, 0.5)',
                      },
                    }}
                  >
                    {submitting ? <CircularProgress size={24} color="inherit" /> : 'Submit Ticket'}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => navigate(-1)}
                    sx={{
                      borderColor: '#cbd5e1',
                      color: '#64748b',
                      px: 3,
                      py: 1.5,
                      borderRadius: '12px',
                      fontWeight: 600,
                      textTransform: 'none',
                      flex: 1,
                      '&:hover': { borderColor: '#94a3b8', bgcolor: 'rgba(0, 0, 0, 0.02)' },
                    }}
                  >
                    Cancel
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Paper>
        )
      )}

      {/* Tab 1: My Support Tickets List */}
      {tabValue === 1 && (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: '24px',
            border: '1px solid rgba(226, 232, 240, 0.8)',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.03)',
            bgcolor: 'white',
          }}
        >
          {/* Filters */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
            <FormControl size="small" sx={{ width: 200 }}>
              <InputLabel sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <FilterList sx={{ fontSize: 18 }} /> Status
              </InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(0);
                }}
                sx={{ borderRadius: '10px' }}
              >
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value="Open">Open</MenuItem>
                <MenuItem value="In Progress">In Progress</MenuItem>
                <MenuItem value="Resolved">Resolved</MenuItem>
                <MenuItem value="Closed">Closed</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Table */}
          {ticketsLoading && tickets.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
              <CircularProgress size={40} sx={{ color: '#283165' }} />
            </Box>
          ) : (
            <>
              <TableContainer sx={{ borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                      <TableCell sx={{ fontWeight: 'bold', color: '#475569' }}>Ticket Ref</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: '#475569' }}>Date Submitted</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: '#475569' }}>Category</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: '#475569' }}>Subject</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: '#475569' }}>Status</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold', color: '#475569' }}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tickets.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                          <Typography color="text.secondary">You haven't submitted any support tickets yet.</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      tickets.map((row) => (
                        <TableRow key={row.id} hover sx={{ cursor: 'pointer' }} onClick={() => setSelectedTicket(row)}>
                          <TableCell sx={{ fontWeight: 'bold', color: '#0f172a' }}>{row.ticketRef}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(row.createdAt)}</TableCell>
                          <TableCell>
                            <Chip size="small" label={row.category} variant="outlined" sx={{ fontWeight: 600, borderColor: '#cbd5e1', color: '#475569' }} />
                          </TableCell>
                          <TableCell sx={{ maxWidth: 300 }}>
                            <Typography variant="body2" fontWeight={500} sx={{ color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {row.subject}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip size="small" label={row.status} color={getStatusColor(row.status)} sx={{ fontWeight: 700 }} />
                          </TableCell>
                          <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => setSelectedTicket(row)}
                              sx={{
                                borderRadius: '8px',
                                textTransform: 'none',
                                borderColor: '#283165',
                                color: '#283165',
                                '&:hover': { bgcolor: 'rgba(40, 49, 101, 0.04)', borderColor: '#1c234b' },
                              }}
                            >
                              View Details
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={pageSize}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50]}
              />
            </>
          )}
        </Paper>
      )}

      {/* Ticket Details Dialog */}
      <Dialog
        open={selectedTicket !== null}
        onClose={() => setSelectedTicket(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: '20px', p: 1 },
        }}
      >
        {selectedTicket && (
          <>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 1 }}>
                  Ticket Details - {selectedTicket.ticketRef}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Submitted on {formatDate(selectedTicket.createdAt)}
                </Typography>
              </Box>
              <IconButton onClick={() => setSelectedTicket(null)}>
                <Close />
              </IconButton>
            </DialogTitle>
            <Divider />
            <DialogContent sx={{ py: 3 }}>
              <Grid container spacing={3}>
                {/* Metadata */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#475569', mb: 1 }}>
                    Category
                  </Typography>
                  <Chip size="medium" label={selectedTicket.category} variant="outlined" sx={{ fontWeight: 600, borderColor: '#cbd5e1', color: '#475569' }} />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#475569', mb: 1 }}>
                    Status
                  </Typography>
                  <Chip size="medium" label={selectedTicket.status} color={getStatusColor(selectedTicket.status)} sx={{ fontWeight: 700 }} />
                </Grid>

                {selectedTicket.caseNumber && (
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#475569', mb: 0.5 }}>
                      Related Case Number
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#1e293b', fontWeight: 600 }}>
                      {selectedTicket.caseNumber}
                    </Typography>
                  </Grid>
                )}

                {/* Subject & Description */}
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#475569', mb: 1 }}>
                    Subject
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: '#1e293b', mb: 2 }}>
                    {selectedTicket.subject}
                  </Typography>

                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#475569', mb: 1 }}>
                    Description
                  </Typography>
                  <Paper sx={{ p: 2.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', whiteSpace: 'pre-wrap' }}>
                    <Typography variant="body2" sx={{ color: '#334155', lineHeight: 1.6 }}>
                      {selectedTicket.description}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ p: 2.5 }}>
              <Button onClick={() => setSelectedTicket(null)} variant="outlined" sx={{ borderRadius: '10px', textTransform: 'none', px: 3 }}>
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default RaiseComplaint;
